import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, LogOut, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [shows, setShows] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentShow, setCurrentShow] = useState(null);
  const [formData, setFormData] = useState({
    date: '',
    displayDate: '',
    time: '',
    venue: '',
    location: '',
    title: '',
    role: 'Performer',
    ticketUrl: '',
    platform: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadShows();
  }, [navigate]);

  const loadShows = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/shows`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShows(response.data);
    } catch (error) {
      console.error('Error loading shows:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      
      if (isEditing && currentShow) {
        await axios.put(`${API}/admin/shows/${currentShow.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Show updated!",
          description: "The show has been updated successfully.",
        });
      } else {
        await axios.post(`${API}/admin/shows`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Show created!",
          description: "New show has been added successfully.",
        });
      }
      
      resetForm();
      loadShows();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save show",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (show) => {
    setCurrentShow(show);
    setFormData({
      date: show.date,
      displayDate: show.displayDate,
      time: show.time,
      venue: show.venue,
      location: show.location,
      title: show.title,
      role: show.role,
      ticketUrl: show.ticketUrl,
      platform: show.platform,
      description: show.description,
      isActive: show.isActive
    });
    setIsEditing(true);
  };

  const handleDelete = async (showId) => {
    if (!window.confirm('Are you sure you want to delete this show?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/shows/${showId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Show deleted!",
        description: "The show has been removed.",
      });
      loadShows();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete show",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      date: '',
      displayDate: '',
      time: '',
      venue: '',
      location: '',
      title: '',
      role: 'Performer',
      ticketUrl: '',
      platform: '',
      description: '',
      isActive: true
    });
    setIsEditing(false);
    setCurrentShow(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink/20 via-white to-burgundy/10 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-playfair font-bold text-burgundy mb-2">
              Admin Panel
            </h1>
            <p className="text-gray-600">Manage your comedy shows</p>
          </div>
          <div className="flex gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-burgundy text-burgundy hover:bg-burgundy hover:text-white"
            >
              View Site
            </Button>
            <Button
              onClick={handleLogout}
              className="bg-burgundy hover:bg-bordeaux text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Form */}
          <Card className="border-2 border-burgundy/30">
            <CardHeader className="bg-gradient-to-br from-pink/20 to-burgundy/10">
              <CardTitle className="text-2xl font-playfair text-burgundy flex items-center gap-2">
                {isEditing ? <Edit className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
                {isEditing ? 'Edit Show' : 'Add New Show'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date">Date (YYYY-MM-DD) *</Label>
                    <Input
                      id="date"
                      name="date"
                      type="date"
                      value={formData.date}
                      onChange={handleChange}
                      required
                      className="border-2 border-gray-300 focus:border-burgundy"
                    />
                  </div>
                  <div>
                    <Label htmlFor="time">Time *</Label>
                    <Input
                      id="time"
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      required
                      placeholder="7:30 PM"
                      className="border-2 border-gray-300 focus:border-burgundy"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="displayDate">Display Date *</Label>
                  <Input
                    id="displayDate"
                    name="displayDate"
                    value={formData.displayDate}
                    onChange={handleChange}
                    required
                    placeholder="Thursday, 12th February 2025"
                    className="border-2 border-gray-300 focus:border-burgundy"
                  />
                </div>

                <div>
                  <Label htmlFor="title">Show Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="border-2 border-gray-300 focus:border-burgundy"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="venue">Venue *</Label>
                    <Input
                      id="venue"
                      name="venue"
                      value={formData.venue}
                      onChange={handleChange}
                      required
                      className="border-2 border-gray-300 focus:border-burgundy"
                    />
                  </div>
                  <div>
                    <Label htmlFor="location">Location *</Label>
                    <Input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      placeholder="Fulham, London"
                      className="border-2 border-gray-300 focus:border-burgundy"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="role">Your Role *</Label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border-2 border-gray-300 rounded-lg focus:border-burgundy focus:outline-none"
                    >
                      <option value="Performer">Performer</option>
                      <option value="MC">MC</option>
                      <option value="Headliner">Headliner</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="platform">Platform *</Label>
                    <Input
                      id="platform"
                      name="platform"
                      value={formData.platform}
                      onChange={handleChange}
                      required
                      placeholder="Eventbrite, Dice, etc."
                      className="border-2 border-gray-300 focus:border-burgundy"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="ticketUrl">Ticket URL *</Label>
                  <Input
                    id="ticketUrl"
                    name="ticketUrl"
                    type="url"
                    value={formData.ticketUrl}
                    onChange={handleChange}
                    required
                    className="border-2 border-gray-300 focus:border-burgundy"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={3}
                    className="border-2 border-gray-300 focus:border-burgundy resize-none"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="w-4 h-4 text-burgundy focus:ring-burgundy"
                  />
                  <Label htmlFor="isActive" className="cursor-pointer">Show is active (visible on website)</Label>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-burgundy to-bordeaux hover:from-bordeaux hover:to-burgundy text-white"
                  >
                    {isEditing ? 'Update Show' : 'Add Show'}
                  </Button>
                  {isEditing && (
                    <Button
                      type="button"
                      onClick={resetForm}
                      variant="outline"
                      className="border-gray-400"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Shows List */}
          <div className="space-y-4">
            <h2 className="text-2xl font-playfair font-bold text-burgundy flex items-center gap-2">
              <Calendar className="w-6 h-6" />
              Current Shows ({shows.length})
            </h2>
            <div className="space-y-4 max-h-[800px] overflow-y-auto pr-2">
              {shows.map((show) => (
                <Card key={show.id} className="border-2 border-burgundy/20 hover:shadow-lg transition-shadow">
                  <CardHeader className="bg-gradient-to-br from-pink/10 to-transparent pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg font-playfair text-burgundy mb-1">
                          {show.title}
                        </CardTitle>
                        <p className="text-sm text-gray-600">{show.venue} • {show.location}</p>
                        <p className="text-sm text-gray-500 mt-1">{show.displayDate} at {show.time}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(show)}
                          className="border-burgundy text-burgundy hover:bg-burgundy hover:text-white"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(show.id)}
                          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
